import React from 'react';
import { Search, Filter, X, Zap } from 'lucide-react';
import { Format, CECategory, CE_CATEGORIES } from '../../data/classes';
interface FindAClassFiltersProps {
  formatFilter: Format | 'All Formats';
  setFormatFilter: (format: Format | 'All Formats') => void;
  categoryFilter: CECategory | 'All Categories';
  setCategoryFilter: (category: CECategory | 'All Categories') => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onReset: () => void;
  onShowNext: () => void;
  resultCount: number;
}
export function FindAClassFilters({
  formatFilter,
  setFormatFilter,
  categoryFilter,
  setCategoryFilter,
  searchQuery,
  setSearchQuery,
  onReset,
  onShowNext,
  resultCount
}: FindAClassFiltersProps) {
  const quickFilters: Array<{
    label: string;
    value: Format | 'All Formats';
  }> = [{
    label: 'All',
    value: 'All Formats'
  }, {
    label: 'Zoom',
    value: 'Zoom'
  }, {
    label: 'In-Person',
    value: 'In-Person'
  }];
  return <div className="bg-brand-purple-50 rounded-xl border border-brand-purple-200 shadow-sm p-6 mb-8" data-id="element-178" data-mp="cj8okrdua79m">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-179" data-mp="hicv1a9dpqw9">
        <div className="flex items-center" data-id="element-180" data-mp="mfbmtm5kllra">
          <Filter className="w-5 h-5 text-brand-purple-600 mr-2" data-id="element-181" data-mp="mir5t02d7exz" />
          <h2 className="text-xl font-bold text-gray-900" data-id="element-182" data-mp="qirm6q90y07o">Find a Class</h2>
        </div>

        {/* Quick Format Filters */}
        <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50 self-start sm:self-auto" role="group" aria-label="Quick format filter" data-id="element-183" data-mp="veyq6dhv11mz">
          {quickFilters.map(qf => <button key={qf.value} onClick={() => setFormatFilter(qf.value)} aria-pressed={formatFilter === qf.value} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple-500 ${formatFilter === qf.value ? 'bg-brand-purple-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-184" data-mp="1a6h3u4n8yk36">
              {qf.label}
            </button>)}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6" data-id="element-185" data-mp="3p0d84vtjz8wc">
        {/* Search */}
        <div className="md:col-span-12 lg:col-span-4" data-id="element-186" data-mp="3k83cr09o5gmo">
          <label htmlFor="search-classes" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-187" data-mp="2sdhbbir2knr">
            Search
          </label>
          <div className="relative" data-id="element-188" data-mp="3u0sn1rthgogf">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-189" data-mp="btlc3jm253d3">
              <Search className="h-5 w-5 text-gray-400" data-id="element-190" data-mp="6tgu1p5i40kr" />
            </div>
            <input type="text" id="search-classes" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-brand-purple-500 focus:border-brand-purple-500 sm:text-sm" placeholder="Search by class title, instructor, city, or topic" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-191" data-mp="6s5s7uvm92wa" />
          </div>
        </div>

        {/* Format Filter */}
        <div className="md:col-span-6 lg:col-span-3" data-id="element-192" data-mp="jahuybaqpjdh">
          <label htmlFor="format-filter" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-193" data-mp="5cjqpyq4v3bp">
            Class Format
          </label>
          <select id="format-filter" className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-brand-purple-500 focus:border-brand-purple-500 sm:text-sm rounded-md" value={formatFilter} onChange={e => setFormatFilter(e.target.value as Format | 'All Formats')} data-id="element-194" data-mp="3shsngzmdd1u7">
            <option value="All Formats" data-id="element-195" data-mp="160ts3mc5l5gy">All Formats</option>
            <option value="In-Person" data-id="element-196" data-mp="160by6wcyr3c7">In-Person</option>
            <option value="Zoom" data-id="element-197" data-mp="111qx6upw0fmp">Zoom</option>
          </select>
        </div>

        {/* Category Filter */}
        <div className="md:col-span-6 lg:col-span-5" data-id="element-198" data-mp="w1w5wq279o14">
          <label htmlFor="category-filter" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-199" data-mp="w4qrwm0v3gwn">
            CE Category
          </label>
          <select id="category-filter" className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-brand-purple-500 focus:border-brand-purple-500 sm:text-sm rounded-md" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value as CECategory | 'All Categories')} data-id="element-200" data-mp="n3k7i1v3dly4">
            <option value="All Categories" data-id="element-201" data-mp="3gcplu2qpyfa">All Categories</option>
            {CE_CATEGORIES.map(cat => <option key={cat} value={cat} data-id="element-202" data-mp="3vkf1ssxtpy0l">
                {cat}
              </option>)}
          </select>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pt-4 border-t border-gray-100" data-id="element-203" data-mp="2f0kdfqenikmv">
        <div className="flex flex-wrap items-center gap-4" data-id="element-204" data-mp="2k180k0zrehbt">
          <span className="text-sm text-gray-600" data-id="element-205" data-mp="2p0b41h03cfho">
            Showing{' '}
            <span className="font-bold text-gray-900" data-id="element-206" data-mp="1rc16be7c8dwd">{resultCount}</span>{' '}
            upcoming classes
          </span>
          <button onClick={onShowNext} className="inline-flex items-center text-sm font-semibold text-brand-purple-700 hover:text-brand-purple-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple-500 rounded" data-id="element-207" data-mp="1wace7je2oeby">
            <Zap className="w-4 h-4 mr-1" data-id="element-208" data-mp="18ljedbubxmc2" />
            Show Next Available Classes
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-209" data-mp="1dkcpwyq2339l">
          <div className="flex items-center text-sm text-gray-600" data-id="element-210" data-mp="1di9svuqhk9vd">
            <span className="w-3 h-3 rounded-full bg-brand-purple-600 mr-1.5" data-id="element-211" data-mp="15idk4zg2mvny"></span>
            Jon Kichen
            <span className="w-3 h-3 rounded-full bg-brand-orange-500 ml-4 mr-1.5" data-id="element-212" data-mp="11i338rqqqkub"></span>
            Other Instructors
          </div>

          <button onClick={onReset} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-purple-500" data-id="element-213" data-mp="1aan47yui91ib">
            <X className="w-4 h-4 mr-1" data-id="element-214" data-mp="wiyvm5jgsv63" />
            Reset Filters
          </button>
        </div>
      </div>
    </div>;
}